import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { MessageCircle, Eye, ChevronLeft, ChevronRight } from 'lucide-react';

const WHATSAPP_NUMBER = "919535834444";

const products = [
  {
    id: 1,
    name: 'Royal Banarasi Silk Saree',
    image: 'https://images.pexels.com/photos/5814563/pexels-photo-5814563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    category: 'Sarees',
  },
  {
    id: 2,
    name: 'Designer Lehenga Choli',
    image: 'https://images.unsplash.com/photo-1767955694884-d4bf352c23c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    category: 'Lehenga',
  },
  {
    id: 3,
    name: 'Embroidered Anarkali Gown',
    image: 'https://images.unsplash.com/photo-1767790693645-2373e54d4f02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    category: 'Gowns',
  },
  {
    id: 4,
    name: 'Traditional Zari Work Saree',
    image: 'https://images.unsplash.com/photo-1760613129745-418b15f91d56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    category: 'Sarees',
  },
  {
    id: 5,
    name: 'Bridal Kanchipuram Silk',
    image: 'https://images.pexels.com/photos/28943676/pexels-photo-28943676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    category: 'Bridal',
  },
  {
    id: 6,
    name: 'Party Wear Crop Top Set',
    image: 'https://images.unsplash.com/photo-1610047614301-13c63f00c032?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBjcm9wJTIwdG9wJTIwZXRobmljJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    category: 'Ethnic Wear',
  },
  {
    id: 7,
    name: 'Designer Palazzo Suit',
    image: 'https://images.unsplash.com/photo-1750481890787-964db32a0d74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBmYXNoaW9uJTIwdG9wc3xlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    category: 'Ethnic Wear',
  },
];

const ProductCard = ({ product, index }) => {
  const getWhatsAppLink = () => {
    const message = encodeURIComponent(`Hi! I'm interested in "${product.name}" from Devanand Vestment. Can you please share more details?`);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  return (
    <motion.div
      className="flex-shrink-0 w-[280px] md:w-[320px] lg:w-[380px] group"
      initial={{ opacity: 0, x: 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-white rounded-2xl" style={{ boxShadow: '0 10px 40px -10px rgba(128, 0, 32, 0.25), 0 0 20px rgba(212, 175, 55, 0.15)' }}>
        {/* Product Image */}
        <div className="relative h-[400px] md:h-[480px] overflow-hidden rounded-t-2xl">
          <motion.img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.6 }}
          />

          {/* Overlay on Hover */}
          <motion.div
            className="absolute inset-0 bg-black/40 flex items-center justify-center gap-4"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Quick View Button */}
            <motion.button
              className="p-3 bg-white text-[#800020] hover:bg-[#D4AF37] hover:text-white transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid={`quick-view-btn-${product.id}`}
            >
              <Eye size={24} />
            </motion.button>

            {/* WhatsApp Button */}
            <motion.a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid={`carousel-whatsapp-btn-${product.id}`}
            >
              <MessageCircle size={24} />
            </motion.a>
          </motion.div>

          {/* Category Badge */}
          <div className="absolute top-4 left-4 px-3 py-1 bg-[#800020] text-white font-body text-xs tracking-wider rounded-full">
            {product.category}
          </div>
        </div>

        {/* Product Info */}
        <div className="p-6 bg-white rounded-b-2xl">
          <h4 className="font-heading text-lg md:text-xl text-[#1A1A1A]">
            {product.name}
          </h4>
        </div>

        {/* Gold Border Effect */}
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          style={{
            boxShadow: '0 0 40px rgba(212, 175, 55, 0.4)',
          }}
        />
      </div>
    </motion.div>
  );
};

export const ProductCarousel = () => {
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="sarees"
      ref={containerRef}
      className="py-24 md:py-32 bg-[#F5F5F0]"
      data-testid="product-carousel-section"
    >
      <div className="max-w-[1920px] mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="font-accent text-sm tracking-[0.3em] text-[#D4AF37] uppercase block mb-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
          >
            Handpicked for You
          </motion.span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A]">
            Luxury Showcase
          </h2>
          <motion.div
            className="w-24 h-0.5 bg-[#D4AF37] mx-auto mt-6"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
        </motion.div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons */}
          <motion.button
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-[#800020] text-white hover:bg-[#600018] transition-colors hidden md:block"
            onClick={() => scroll('left')}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft size={24} />
          </motion.button>
          <motion.button
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-[#800020] text-white hover:bg-[#600018] transition-colors hidden md:block"
            onClick={() => scroll('right')}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            data-testid="carousel-next-btn"
          >
            <ChevronRight size={24} />
          </motion.button>

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scroll-smooth px-4 md:px-12 pb-4 horizontal-scroll"
          >
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
